// transformLabel.ts
export function transformLabel(copy: string): string {
	if (!copy) return copy;

	// Find the first "label:" where ':' occurs in text (not inside HTML tags).
	let inTag = false;
	let labelStart = -1;
	let colon = -1;

	for (let i = 0; i < copy.length; i++) {
		const ch = copy[i];
		if (ch === '<') inTag = true;
		if (!inTag) {
			if (labelStart < 0 && ch.trim()) labelStart = i;
			if (ch === ':') {
				colon = i;
				break;
			}
		}
		if (ch === '>') inTag = false;
	}

	if (labelStart < 0 || colon < 0) return copy;

	const prefix = copy.slice(0, labelStart);
	const labelRegion = copy.slice(labelStart, colon + 1);
	const after = copy.slice(colon + 1);

	const labelText = labelRegion.replace(/<[^>]*>/g, '').trim();
	if (!labelText.endsWith(':')) return copy;

	// Void tags never have closing tags; treating them as self-closing prevents stack corruption.
	const VOID = new Set([
		'area',
		'base',
		'br',
		'col',
		'embed',
		'hr',
		'img',
		'input',
		'link',
		'meta',
		'param',
		'source',
		'track',
		'wbr',
	]);

	const deferred: string[] = [];
	const stack: { name: string; pos: number }[] = [];
	let hasStrongWrapper = false;

	// We only care about tags up to the colon; closers there can “interleave” with the label.
	const tagRe = /<\s*(\/?)\s*([a-zA-Z0-9:-]+)[^>]*?>/g;
	for (const m of copy.slice(0, colon + 1).matchAll(tagRe)) {
		const raw = m[0];
		const isClose = !!m[1];
		const name = m[2].toLowerCase();
		const tagPos = m.index ?? 0;

		const selfClose = !isClose && (VOID.has(name) || /\/\s*>$/.test(raw));
		if (!isClose && !selfClose) {
			stack.push({ name, pos: tagPos });
			continue;
		}

		if (isClose) {
			for (let s = stack.length - 1; s >= 0; s--) {
				if (stack[s].name === name) {
					const open = stack.splice(s, 1)[0];
					// If a wrapper opens before the label but closes within the label region,
					// defer its closer to after we inject <strong> so we don't produce malformed HTML.
					if (open.pos < labelStart && tagPos >= labelStart) {
						deferred.push(raw);
						// If the label is already inside a <strong> opened before the label,
						// we should reuse it instead of double-wrapping.
						if (name === 'strong') hasStrongWrapper = true;
					}
					break;
				}
			}
		}
	}

	const upper = labelText.toUpperCase();
	return hasStrongWrapper
		? `${prefix}${upper}${deferred.join('')}${after}`
		: `${prefix}<strong>${upper}</strong>${deferred.join('')}${after}`;
}
